
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileSpreadsheet, Upload, Info, AlertCircle, Check, Database } from "lucide-react";
import { parseCsvFile, importClients, type CsvClient } from "@/utils/csvImport";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";

interface ImportWizardProps {
  onImportSuccess: () => void;
  onClose: () => void;
}

type Platform = "squire" | "glossgenius" | "vagaro" | "custom";
type ImportStep = "select-platform" | "upload" | "preview" | "map-fields" | "import";

export function ImportWizard({ onImportSuccess, onClose }: ImportWizardProps) {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [step, setStep] = useState<ImportStep>("select-platform");
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [mappedClients, setMappedClients] = useState<CsvClient[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [progress, setProgress] = useState(0);

  const platformTemplates = {
    squire: {
      name: "Squire",
      description: "Import client data from Squire",
      fieldMap: {
        "First Name": "first_name",
        "Last Name": "last_name",
        "Email": "email",
        "Phone Number": "phone",
        "Client Notes": "notes"
      }
    },
    glossgenius: {
      name: "GlossGenius",
      description: "Import client data from GlossGenius",
      fieldMap: {
        "First Name": "first_name",
        "Last Name": "last_name",
        "Email Address": "email",
        "Phone": "phone",
        "Notes": "notes"
      }
    },
    vagaro: {
      name: "Vagaro",
      description: "Import client data from Vagaro",
      fieldMap: {
        "First": "first_name",
        "Last": "last_name",
        "Email": "email",
        "Mobile Phone": "phone",
        "Client Notes": "notes"
      }
    },
    custom: {
      name: "Custom CSV",
      description: "Import data from a custom formatted CSV file",
      fieldMap: {}
    }
  };

  const requiredFields = ["first_name", "last_name"];
  const optionalFields = ["email", "phone", "notes"];

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsProcessing(true);

    try {
      const data = await parseCsvFile(selectedFile);
      setParsedData(data);
      
      // Auto-map columns if platform is selected
      if (selectedPlatform && selectedPlatform !== "custom") {
        const template = platformTemplates[selectedPlatform];
        const newMapping: Record<string, string> = {};
        
        // Get CSV headers
        if (data.length > 0) {
          const headers = Object.keys(data[0]);
          
          // Try to automatically map based on the template
          headers.forEach(header => {
            const mappedField = Object.entries(template.fieldMap)
              .find(([templateHeader]) => 
                templateHeader.toLowerCase() === header.toLowerCase()
              );
            
            if (mappedField) {
              newMapping[header] = mappedField[1];
            }
          });
          
          setColumnMapping(newMapping);
        }
      }
      
      setStep("preview");
    } catch (error) {
      console.error("CSV parsing error:", error);
      toast.error("Failed to parse CSV file. Please check the format.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleColumnMappingChange = (csvColumn: string, figgyCrmField: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [csvColumn]: figgyCrmField
    }));
  };

  const mapDataToClients = () => {
    if (parsedData.length === 0) return [];
    
    const mappedData: CsvClient[] = parsedData.map(row => {
      const client: CsvClient = {
        first_name: "",
        last_name: "",
        email: undefined,
        phone: undefined,
        notes: undefined
      };
      
      // Map each column based on the user's mapping
      Object.entries(columnMapping).forEach(([csvColumn, figgyCrmField]) => {
        if (figgyCrmField in client && row[csvColumn] !== undefined) {
          (client as any)[figgyCrmField] = row[csvColumn];
        }
      });
      
      return client;
    });
    
    // Filter out clients with missing required fields
    return mappedData.filter(client => 
      client.first_name && client.first_name.trim() !== "" && 
      client.last_name && client.last_name.trim() !== ""
    );
  };

  const handleProceedToMapping = () => {
    setStep("map-fields");
  };

  const handleProceedToImport = () => {
    const clients = mapDataToClients();
    setMappedClients(clients);
    setStep("import");
  };

  const handleImport = async () => {
    if (mappedClients.length === 0) {
      toast.error("No valid clients to import");
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Import in batches to show progress
      const batchSize = 50;
      const totalBatches = Math.ceil(mappedClients.length / batchSize);
      
      for (let i = 0; i < totalBatches; i++) {
        const start = i * batchSize;
        const end = Math.min(start + batchSize, mappedClients.length);
        const batch = mappedClients.slice(start, end);
        
        await importClients(batch);
        
        // Update progress
        setProgress(Math.round(((i + 1) / totalBatches) * 100));
      }
      
      toast.success(`Successfully imported ${mappedClients.length} clients`);
      onImportSuccess();
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import clients. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderSelectPlatformStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(Object.keys(platformTemplates) as Platform[]).map(platform => (
          <Card 
            key={platform}
            className={`cursor-pointer hover:shadow-md transition-shadow ${selectedPlatform === platform ? 'ring-2 ring-figgy-500' : ''}`}
            onClick={() => setSelectedPlatform(platform)}
          >
            <CardHeader className="pb-2">
              <CardTitle>{platformTemplates[platform].name}</CardTitle>
              <CardDescription>{platformTemplates[platform].description}</CardDescription>
            </CardHeader>
            <CardFooter className="pt-2">
              <FileSpreadsheet className="h-5 w-5 mr-2 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {platform === "custom" ? "Custom Format" : "Template Available"}
              </span>
            </CardFooter>
          </Card>
        ))}
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={() => setStep("upload")} 
          disabled={!selectedPlatform}
          className="bg-figgy-500 hover:bg-figgy-600"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Your CSV File</CardTitle>
          <CardDescription>
            {selectedPlatform !== "custom" 
              ? `We'll help you import your ${platformTemplates[selectedPlatform!].name} data` 
              : "Upload your custom formatted CSV file"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 px-4 border-2 border-dashed rounded-lg">
            <Upload className="h-10 w-10 text-muted-foreground mb-4" />
            
            <p className="text-center text-muted-foreground mb-4">
              Drag and drop your CSV file here, or click to browse
            </p>
            
            <label className="cursor-pointer">
              <Input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileUpload}
                disabled={isProcessing}
              />
              <Button 
                variant="outline" 
                className="w-full"
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Select CSV File'}
              </Button>
            </label>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-xs text-muted-foreground">
            Maximum file size: 5MB
          </div>
          
          <Button variant="outline" onClick={() => setStep("select-platform")}>
            Back
          </Button>
        </CardFooter>
      </Card>
      
      {selectedPlatform !== "custom" && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>CSV Export Instructions</AlertTitle>
          <AlertDescription>
            <p className="mb-2">Here's how to export your client data from {platformTemplates[selectedPlatform!].name}:</p>
            {selectedPlatform === "squire" && (
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                <li>Log in to your Squire dashboard</li>
                <li>Go to Clients section</li>
                <li>Click on Export button (usually in the top right)</li>
                <li>Select CSV format and download</li>
              </ol>
            )}
            {selectedPlatform === "glossgenius" && (
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                <li>Log in to your GlossGenius account</li>
                <li>Navigate to Clients</li>
                <li>Click on the export option</li>
                <li>Choose CSV format and export your data</li>
              </ol>
            )}
            {selectedPlatform === "vagaro" && (
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                <li>Log in to your Vagaro account</li>
                <li>Go to Customers section</li>
                <li>Select Export feature</li>
                <li>Choose CSV as the export format</li>
              </ol>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderPreviewStep = () => {
    if (parsedData.length === 0) return <div>No data found in CSV file</div>;
    
    const headers = Object.keys(parsedData[0]);
    const previewRows = parsedData.slice(0, 5);
    
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Preview Your Data</CardTitle>
            <CardDescription>
              We found {parsedData.length} rows in your CSV file. Here's a preview of the first 5.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {headers.map(header => (
                    <TableHead key={header}>{header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewRows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {headers.map(header => (
                      <TableCell key={`${rowIndex}-${header}`}>
                        {row[header]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
          <CardFooter className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setStep("upload")}>
              Back
            </Button>
            <Button 
              onClick={handleProceedToMapping}
              className="bg-figgy-500 hover:bg-figgy-600"
            >
              Continue
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  };

  const renderMappingStep = () => {
    if (parsedData.length === 0) return <div>No data found</div>;
    
    const csvHeaders = Object.keys(parsedData[0]);
    
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Map Your CSV Columns</CardTitle>
            <CardDescription>
              Tell us which columns from your CSV file match our client fields
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="font-medium">CSV Column</div>
                <div className="font-medium">Figgy CRM Field</div>
              </div>
              
              {csvHeaders.map(header => (
                <div key={header} className="grid grid-cols-2 gap-4 items-center">
                  <div className="truncate">{header}</div>
                  <Select
                    value={columnMapping[header] || ""}
                    onValueChange={(value) => handleColumnMappingChange(header, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">-- Ignore this column --</SelectItem>
                      <SelectItem value="first_name">First Name (Required)</SelectItem>
                      <SelectItem value="last_name">Last Name (Required)</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="notes">Notes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between pt-4">
            <Alert variant="destructive" className="max-w-md">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Required Fields</AlertTitle>
              <AlertDescription>
                First Name and Last Name are required for import
              </AlertDescription>
            </Alert>
            
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => setStep("preview")}>
                Back
              </Button>
              <Button 
                onClick={handleProceedToImport}
                className="bg-figgy-500 hover:bg-figgy-600"
                disabled={
                  !Object.values(columnMapping).includes("first_name") ||
                  !Object.values(columnMapping).includes("last_name")
                }
              >
                Continue
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  };

  const renderImportStep = () => {
    const validClientCount = mappedClients.length;
    const invalidClientCount = parsedData.length - validClientCount;
    
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Import Clients</CardTitle>
            <CardDescription>
              Review your import before proceeding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Total records found</p>
                <p className="text-2xl font-bold">{parsedData.length}</p>
              </div>
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <p className="font-medium text-green-800">Valid clients to import</p>
                <p className="text-2xl font-bold text-green-700">{validClientCount}</p>
              </div>
              <Check className="h-8 w-8 text-green-600" />
            </div>
            
            {invalidClientCount > 0 && (
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                <div>
                  <p className="font-medium text-yellow-800">Records skipped (missing required fields)</p>
                  <p className="text-2xl font-bold text-yellow-700">{invalidClientCount}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              </div>
            )}
            
            {isProcessing && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Importing clients...</p>
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-right text-muted-foreground">
                  {progress}% complete
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end space-x-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setStep("map-fields")}
              disabled={isProcessing}
            >
              Back
            </Button>
            <Button 
              onClick={handleImport}
              className="bg-figgy-500 hover:bg-figgy-600"
              disabled={validClientCount === 0 || isProcessing}
            >
              {isProcessing ? 'Importing...' : `Import ${validClientCount} Clients`}
            </Button>
          </CardFooter>
        </Card>
        
        <Alert>
          <Database className="h-4 w-4" />
          <AlertTitle>What happens next?</AlertTitle>
          <AlertDescription>
            Clients will be imported to your CRM. You can then manage them, 
            send messages, and track their appointments from the Clients page.
          </AlertDescription>
        </Alert>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (step) {
      case "select-platform":
        return renderSelectPlatformStep();
      case "upload":
        return renderUploadStep();
      case "preview":
        return renderPreviewStep();
      case "map-fields":
        return renderMappingStep();
      case "import":
        return renderImportStep();
      default:
        return null;
    }
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-2">
          <div className={`h-2 w-2 rounded-full ${step === "select-platform" ? "bg-figgy-500" : "bg-gray-300"}`} />
          <div className={`h-2 w-16 ${step === "upload" || step === "preview" || step === "map-fields" || step === "import" ? "bg-figgy-500" : "bg-gray-300"}`} />
          <div className={`h-2 w-2 rounded-full ${step === "upload" || step === "preview" ? "bg-figgy-500" : "bg-gray-300"}`} />
          <div className={`h-2 w-16 ${step === "map-fields" || step === "import" ? "bg-figgy-500" : "bg-gray-300"}`} />
          <div className={`h-2 w-2 rounded-full ${step === "map-fields" ? "bg-figgy-500" : "bg-gray-300"}`} />
          <div className={`h-2 w-16 ${step === "import" ? "bg-figgy-500" : "bg-gray-300"}`} />
          <div className={`h-2 w-2 rounded-full ${step === "import" ? "bg-figgy-500" : "bg-gray-300"}`} />
        </div>
        
        <div className="grid grid-cols-4 text-xs text-muted-foreground">
          <div className={step === "select-platform" ? "font-medium text-figgy-500" : ""}>Select Platform</div>
          <div className={step === "upload" || step === "preview" ? "font-medium text-figgy-500" : ""}>Upload CSV</div>
          <div className={step === "map-fields" ? "font-medium text-figgy-500" : ""}>Map Fields</div>
          <div className={step === "import" ? "font-medium text-figgy-500" : ""}>Import</div>
        </div>
      </div>
      
      {renderCurrentStep()}
    </div>
  );
}
